import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CatMark } from "@/components/CatMark";

function scrollTo(id) {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis && typeof window.__lenis.scrollTo === "function") {
    window.__lenis.scrollTo(el, { offset: -40, duration: 1.4 });
  } else {
    el.scrollIntoView({ behavior: "smooth" });
  }
}

function Logo({ className = "" }) {
  return (
    <div className={`flex items-start gap-2 ${className}`}>
      <CatMark size={30} stroke={1.4} color="currentColor" className="mt-[3px]" />
      <div className="flex flex-col leading-none">
        <span className="font-display text-[15px] tracking-tight">
          La Michi <span className="font-italic-display">Agencia</span>
        </span>
        <span className="font-italic-display text-[12px] mt-1 opacity-70">
          Agencia creativa
        </span>
      </div>
    </div>
  );
}

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "studio", label: "Estudio", n: "01" },
    { id: "services", label: "Disciplinas", n: "02" },
    { id: "work", label: "Destinos", n: "03" },
    { id: "pricing", label: "Tarifas", n: "04" },
    { id: "faq", label: "Preguntas", n: "05" },
    { id: "contact", label: "Contacto", n: "06" },
  ];

  return (
    <>
      <motion.nav
        data-testid="site-nav"
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 2.0, duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
        className={`fixed top-0 left-0 right-0 z-[100] transition-colors duration-500 ${
          scrolled ? "backdrop-blur-md bg-[#FAF8F5]/70" : ""
        }`}
      >
        <div className="max-w-[95%] mx-auto flex items-center justify-between py-5">
          <a href="#top" onClick={(e) => { e.preventDefault(); scrollTo("top"); }} data-testid="nav-logo" aria-label="Ir al inicio">
            <Logo />
          </a>

          <div className="hidden md:flex items-center gap-8">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => scrollTo(l.id)}
                className="group flex items-baseline gap-2 font-sans text-[13px] tracking-tight"
              >
                <span className="text-[10px] tracking-[0.2em] opacity-40 group-hover:opacity-100 transition-opacity duration-500">
                  {l.n}
                </span>
                <span className="link-underline">{l.label}</span>
              </button>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-4">
            <span className="font-sans text-[11px] tracking-[0.2em] uppercase opacity-60">Barcelona · ES</span>
            <button
              data-testid="nav-cta"
              onClick={() => scrollTo("contact")}
              className="btn-primary !py-2.5 !px-4 !text-[12px]"
            >
              Hablemos
            </button>
          </div>

          <button
            className="md:hidden font-sans text-[13px] tracking-tight"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-menu-toggle"
            aria-label="Menú"
          >
            {open ? "Cerrar" : "Menú"}
          </button>
        </div>
      </motion.nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[99] md:hidden"
            style={{ background: "#111111", color: "#FAF8F5" }}
            initial={{ y: "-100%" }}
            animate={{ y: 0 }}
            exit={{ y: "-100%" }}
            transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
          >
            <div className="pt-24 px-6 flex flex-col gap-4">
              {links.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.15 + i * 0.06, duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
                  onClick={() => { setOpen(false); setTimeout(() => scrollTo(l.id), 350); }}
                  className="text-left font-display text-5xl leading-[1] tighter"
                >
                  <span className="text-[12px] font-sans opacity-40 mr-3 align-super">{l.n}</span>
                  {l.label}
                </motion.button>
              ))}
              <div className="mt-10 font-sans text-[11px] tracking-[0.22em] uppercase opacity-60">
                hello@lamichiagencia.com
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
